import React, { useState, useEffect } from 'react';
import * as Realm from 'realm-web';

const app = new Realm.App({ id: "thebrainfund-qyrogsg" });

function ModelTemplates({ onSelectTemplate }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await app.currentUser.functions.getModels({
        filter: { "metadata.category": "Template" },
        sort: { createdAt: -1 },
        limit: 20
      });

      if (result.success) {
        console.log('Templates result:', result); // Debug log
        console.log('Templates models:', result.models); // Debug log
        
        if (!result.models || result.models.length === 0) {
          console.log('No templates found');
          setTemplates([]);
          return;
        }
        
        // Transform database models to match the expected template structure
        const transformedTemplates = result.models.map(model => {
          console.log('Processing model:', model); // Debug log
          return {
            id: model._id ? model._id.toString() : model.id || Math.random().toString(),
            name: model.name || 'Unnamed Template',
            description: model.description || 'No description available',
            icon: model.icon || '📊',
            difficulty: model.difficulty || 'Intermediate',
            variables: model.variables || [],
            stats: {
              backtested_games: model.stats?.backtested_games || 0,
              win_rate: model.stats?.win_rate || 0,
              roi: model.stats?.roi || 0,
              max_drawdown: model.stats?.max_drawdown || 0
            }
          };
        });

        setTemplates(transformedTemplates);
      } else {
        setError('Failed to load templates');
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error loading templates. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return '#10b981';
      case 'Intermediate': return '#f59e0b';
      case 'Advanced': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getROIColor = (roi) => {
    if (roi >= 10) return '#10b981';
    if (roi >= 5) return '#f59e0b';
    return '#ef4444';
  };

  return (
    <div className="model-templates">
      <div className="templates-header">
        <h2>Model Templates</h2>
        <p>Get started quickly with pre-built prediction models. Each template includes optimized variable weights based on historical backtesting.</p>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading templates...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>{error}</p>
          <button onClick={fetchTemplates} className="retry-btn">Try Again</button>
        </div>
      )}

      {!loading && !error && (
        <div className="templates-grid">
          {templates.map(template => (
          <div key={template.id} className="template-card">
            <div className="template-header">
              <div className="template-icon">{template.icon}</div>
              <div className="template-title">
                <h3>{template.name}</h3>
                <span 
                  className="difficulty-badge"
                  style={{ backgroundColor: getDifficultyColor(template.difficulty) }}
                >
                  {template.difficulty}
                </span>
              </div>
            </div>

            <p className="template-description">{template.description}</p>

            <div className="template-variables">
              <h4>Key Variables:</h4>
              <div className="variable-chips">
                {template.variables.map(variable => (
                  <div key={variable.id} className="variable-chip">
                    <span className="variable-chip-name">{variable.name}</span>
                    <span className="variable-chip-weight">{variable.weight}%</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="template-stats">
              <div className="stat-grid">
                <div className="stat-item">
                  <span className="stat-label">Backtested Games</span>
                  <span className="stat-value">{template.stats.backtested_games.toLocaleString()}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Win Rate</span>
                  <span className="stat-value">{template.stats.win_rate}%</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">ROI</span>
                  <span 
                    className="stat-value"
                    style={{ color: getROIColor(template.stats.roi) }}
                  >
                    {template.stats.roi > 0 ? '+' : ''}{template.stats.roi}%
                  </span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Max Drawdown</span>
                  <span className="stat-value">{template.stats.max_drawdown}%</span>
                </div>
              </div>
            </div>

            <button 
              className="template-select-btn"
              onClick={() => onSelectTemplate(template)}
            >
              Use This Template
            </button>
          </div>
        ))}
        </div>
      )}
    </div>
  );
}

export default ModelTemplates;
